import React, { useEffect, useState } from 'react';
import { commApi } from './commApi';
import { ROLE_LABELS, fmtFull } from './format';

const EVENTS = {
	message_sent: 'Αποστολή μηνύματος',
	message_read: 'Ανάγνωση μηνυμάτων',
	conversation_started: 'Νέα συνομιλία',
	conversation_closed: 'Κλείσιμο συνομιλίας',
	conversation_reopened: 'Άνοιγμα συνομιλίας',
	conversation_archived: 'Αρχειοθέτηση',
	permission_changed: 'Αλλαγή δικαιωμάτων',
	user_exception_changed: 'Εξαίρεση χρήστη',
	chat_enabled: 'Ενεργοποίηση Chat',
	chat_disabled: 'Απενεργοποίηση Chat',
	settings_changed: 'Αλλαγή ρυθμίσεων',
	access_denied: 'Άρνηση πρόσβασης',
};

// Read-only audit log with filters: city (Super Admin), user id, event, dates.
export const CommAudit = ({ cities, isSuperAdmin }) => {
	const [filters, setFilters] = useState({
		cityId: '',
		userId: '',
		eventType: '',
		from: '',
		to: '',
	});
	const [rows, setRows] = useState(null);
	const [hasMore, setHasMore] = useState(false);

	const load = async (offset = 0) => {
		const res = await commApi.audit({
			...filters,
			to: filters.to ? `${filters.to}T23:59:59` : '',
			limit: 50,
			offset,
		});
		if (res?.error) {
			setRows([]);
			return;
		}
		setRows(prev => (offset ? [...(prev || []), ...res.events] : res.events));
		setHasMore(res.hasMore);
	};

	const filterKey = JSON.stringify(filters);
	useEffect(() => {
		let alive = true;
		const f = JSON.parse(filterKey);
		commApi.audit({ ...f, to: f.to ? `${f.to}T23:59:59` : '', limit: 50, offset: 0 }).then(res => {
			if (!alive) return;
			setRows(res?.error ? [] : res.events);
			setHasMore(!!res?.hasMore);
		});
		return () => {
			alive = false;
		};
	}, [filterKey]);

	const set = key => e => setFilters(f => ({ ...f, [key]: e.target.value }));
	const cityName = id => cities?.find(c => c.city_id === id)?.name || id || '—';

	return (
		<div className='flex flex-col gap-3'>
			<div className='flex flex-wrap items-center gap-2 text-[14px]'>
				{isSuperAdmin && (
					<select
						className='h-9 rounded-md border border-slate-300 px-2'
						value={filters.cityId}
						onChange={set('cityId')}
					>
						<option value=''>Όλες οι πόλεις</option>
						{(cities || []).map(c => (
							<option key={c.city_id} value={c.city_id}>
								{c.name}
							</option>
						))}
					</select>
				)}
				<select
					className='h-9 rounded-md border border-slate-300 px-2'
					value={filters.eventType}
					onChange={set('eventType')}
				>
					<option value=''>Όλες οι ενέργειες</option>
					{Object.entries(EVENTS).map(([k, v]) => (
						<option key={k} value={k}>
							{v}
						</option>
					))}
				</select>
				<input
					className='h-9 w-28 rounded-md border border-slate-300 px-2'
					placeholder='User ID'
					inputMode='numeric'
					value={filters.userId}
					onChange={set('userId')}
				/>
				<input
					type='date'
					className='h-9 rounded-md border border-slate-300 px-2'
					value={filters.from}
					onChange={set('from')}
				/>
				<span>έως</span>
				<input
					type='date'
					className='h-9 rounded-md border border-slate-300 px-2'
					value={filters.to}
					onChange={set('to')}
				/>
			</div>
			<div className='overflow-x-auto rounded-xl border border-slate-200 bg-white p-3'>
				<table className='w-full text-[13px]'>
					<thead>
						<tr className='border-b border-slate-200 text-left text-slate-500'>
							<th className='py-2 pr-3 font-normal'>Πότε</th>
							<th className='py-2 pr-3 font-normal'>Ενέργεια</th>
							<th className='py-2 pr-3 font-normal'>Χρήστης</th>
							<th className='py-2 pr-3 font-normal'>Πόλη</th>
							<th className='py-2 pr-3 font-normal'>Στόχος</th>
							<th className='py-2 font-normal'>Λεπτομέρειες</th>
						</tr>
					</thead>
					<tbody>
						{(rows || []).map(r => (
							<tr key={r.id} className='border-b border-slate-100 align-top'>
								<td className='py-1.5 pr-3 whitespace-nowrap'>{fmtFull(r.created_at)}</td>
								<td className='py-1.5 pr-3'>{EVENTS[r.event_type] || r.event_type}</td>
								<td className='py-1.5 pr-3'>
									{r.userName || '—'}
									{r.userRole && (
										<span className='text-slate-400'> · {ROLE_LABELS[r.userRole]}</span>
									)}
									<span className='text-slate-400'> #{r.user_id}</span>
								</td>
								<td className='py-1.5 pr-3'>{cityName(r.city_id)}</td>
								<td className='py-1.5 pr-3'>
									{r.target_type ? `${r.target_type} #${r.target_id ?? ''}` : '—'}
								</td>
								<td className='max-w-[360px] py-1.5 break-all text-slate-500'>
									{r.metadata ? JSON.stringify(r.metadata) : ''}
								</td>
							</tr>
						))}
					</tbody>
				</table>
				{rows && !rows.length && (
					<div className='p-4 text-center text-slate-400'>Κανένα γεγονός.</div>
				)}
				{hasMore && (
					<button
						type='button'
						className='mt-2 text-[13px] text-[#0f9b9d]'
						onClick={() => load(rows.length)}
					>
						Περισσότερα
					</button>
				)}
			</div>
		</div>
	);
};
